import { Icon, Tooltip } from "@blueprintjs/core"
import cc from "classcat"
import numbro from "numbro"
import React, { useEffect, useState } from "react"

import styles from "../ProjectBudgetForm.module.css"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"
import { dashify } from "~/helpers/general-helpers"

import CurrencyInput from "~/common/CurrencyInput"
import { Delete } from "~/common/react-icons"

import { BudgetedRole, ProjectRate } from "~/forms/ProjectBudgetForm/types"

import Column from "../TableColumn"
import Row from "../TableRow"

type Props = {
  projectId: number
  budgetedRole: BudgetedRole
  rateCardRate: number
  defaultFullTimeMinutes: number
  projectRate?: ProjectRate
  hideFinancials: boolean
  handleDelete: (id: number) => void
  onRatesUpdate: (roleRate: ProjectRate[]) => void
  showDayView: boolean
}

const ProjectRateItemRow = (props: Props) => {
  const {
    projectId,
    budgetedRole,
    rateCardRate,
    hideFinancials,
    handleDelete,
    onRatesUpdate,
    showDayView,
    projectRate,
    defaultFullTimeMinutes,
  } = props

  const hoursPerDay = defaultFullTimeMinutes / 60

  const originalRate = projectRate?.rate ?? rateCardRate
  const [rateInput, setRateInput] = useState(originalRate)

  useEffect(() => {
    setRateInput(originalRate)
  }, [originalRate])

  const onRateBlur = (e) => {
    if (Number.isNaN(numbro.unformat(e.target.value)) || !e.target.value) {
      setRateInput(rateCardRate)
    }
  }

  const handleRateChange = (val) => {
    if (showDayView) {
      setRateInput(val / hoursPerDay)
    } else {
      setRateInput(val)
    }
  }

  useEffect(() => {
    // Make sure if we update the budget, we also save the rate input.
    // This is important if project_rate doesn't exist yet.
    onRatesUpdate([
      {
        id: projectRate?.id || null,
        roleId: budgetedRole.id,
        projectId: projectId,
        rate: Number(rateInput),
      },
    ])
  }, [rateInput]) // eslint-disable-line react-hooks/exhaustive-deps

  const projectRateNumber = showDayView
    ? (Number(rateInput) * defaultFullTimeMinutes) / 60
    : Number(rateInput)
  const formattedRoleName = dashify(budgetedRole.name)
  const canTransferRate = rateCardRate !== rateInput
  const isScheduled = budgetedRole.isScheduled

  return (
    <Row dataTest={`${formattedRoleName}-rate-row`}>
      <Column>{budgetedRole.name}</Column>
      {!hideFinancials && (
        <>
          <Column>
            <span data-test={`${formattedRoleName}-rate-card-rate`}>
              {formatCurrency(
                showDayView ? rateCardRate * hoursPerDay : rateCardRate,
              )}
            </span>
          </Column>
          <Column>
            {canTransferRate && (
              <Tooltip content={"Copy rate to project"} placement="top">
                <Icon
                  icon="arrow-right"
                  className={styles.transferRateArrow}
                  onClick={() => {
                    setRateInput(rateCardRate)
                  }}
                />
              </Tooltip>
            )}
          </Column>
          <Column>
            <CurrencyInput
              name={`${formattedRoleName}-project_rate`}
              dataTest={`${formattedRoleName}-project_rate`}
              value={projectRateNumber}
              onChange={handleRateChange}
              onBlur={onRateBlur}
              currency={currency}
              height={30}
              selectAllOnFocus
            />
          </Column>
          <div
            className={cc([
              styles.deleteIconContainer,
              { [styles.disabled]: isScheduled },
            ])}
            onClick={() => !isScheduled && handleDelete(budgetedRole.id)}
            data-test="budget-role-delete"
          >
            <Delete
              alt={
                isScheduled
                  ? `Roles that are currently scheduled cannot be removed from the budget allocation.`
                  : `Remove budget allocation for "${budgetedRole.name}"`
              }
              color="var(--winter)"
            />
          </div>
        </>
      )}
    </Row>
  )
}
export default ProjectRateItemRow
